import React, { useState } from 'react';
import { notifyForm } from '@/lib/notifyForm';
import { hubRegions, HUB_IMG } from '@/data/romaniaHub';
import { Check, Loader2 } from 'lucide-react';

const TRAVEL_STYLES = ['Luxury', 'Adventure', 'Cultural', 'Romantic', 'Family', 'Wellness', 'Food & Wine'];
const BUDGETS = ['$5k – $10k', '$10k – $20k', '$20k – $40k', '$40k+'];

const RomaniaInquiry: React.FC = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', dates: '', travelers: '2',
    style: 'Luxury', destinations: '', budget: '$10k – $20k', interests: '', message: '',
  });
  const [smsOptIn, setSmsOptIn] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setError('Please share your name and email so we can reach you.');
      return;
    }
    setError('');
    setSubmitting(true);

    const [firstName, ...rest] = form.name.trim().split(' ');
    await notifyForm({
      email: form.email.trim(),
      firstName,
      lastName: rest.join(' ') || undefined,
      phone: form.phone.trim() || undefined,
      source: 'plan-journey',
      subject: 'Romania Hub — Plan My Journey',
      details: {
        travel_dates: form.dates,
        travelers: form.travelers,
        travel_style: form.style,
        preferred_destinations: form.destinations,
        budget_range: form.budget,
        special_interests: form.interests,
        message: form.message,
        sms_opt_in: smsOptIn,
        hub: 'Romania',
      },
    });

    // Also register in CRM with phone + sms opt-in directly.
    try {
      await fetch('https://famous.ai/api/crm/69cbf6b38dc4f4af5d55476d/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email.trim(),
          name: form.name.trim(),
          phone: form.phone.trim() || undefined,
          sms_opt_in: smsOptIn,
          source: 'plan-journey',
          tags: ['romania-hub', 'plan-journey'],
        }),
      });
    } catch { /* non-blocking */ }

    setSubmitting(false);
    setDone(true);
  };

  const inputCls =
    'w-full bg-white/5 border border-white/15 focus:border-[#C9A961] focus:outline-none font-sans text-sm text-white placeholder-white/40 px-4 py-3 transition-colors';
  const labelCls = 'block font-sans text-[10px] tracking-[0.2em] uppercase text-white/60 mb-2';

  return (
    <section id="plan-journey" className="relative py-24 lg:py-32 overflow-hidden">
      <img src={HUB_IMG.heroB} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-[#1A3B5C]/92" />

      <div className="relative max-w-3xl mx-auto px-6 lg:px-12 text-white">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-[1px] bg-[#C9A961]" />
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">
              Private Consultation
            </span>
            <div className="w-12 h-[1px] bg-[#C9A961]" />
          </div>
          <h2 className="font-serif text-3xl md:text-5xl font-light mb-5">
            Let&apos;s Design Your Perfect Romania Journey
          </h2>
          <p className="font-sans text-sm text-white/60 max-w-xl mx-auto leading-relaxed">
            Share a few details and a Zera Travel designer will craft a bespoke proposal,
            entirely tailored to you.
          </p>
        </div>

        {done ? (
          <div className="bg-white/5 border border-[#C9A961]/40 p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-[#C9A961] flex items-center justify-center mx-auto mb-6">
              <Check className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-serif text-2xl font-light mb-3">Thank You, {form.name.split(' ')[0]}</h3>
            <p className="font-sans text-sm text-white/60 leading-relaxed">
              Your inquiry is with our travel design team. Expect a personal response within 24 hours
              to begin planning your Romania journey.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white/5 border border-white/10 p-7 md:p-10 space-y-6">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className={labelCls}>Name *</label>
                <input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Your full name" />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" className={inputCls} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@email.com" />
              </div>
              <div>
                <label className={labelCls}>Phone number (optional)</label>
                <input type="tel" className={inputCls} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+1 555 000 0000" />
              </div>
              <div>
                <label className={labelCls}>Travel Dates</label>
                <input className={inputCls} value={form.dates} onChange={(e) => set('dates', e.target.value)} placeholder="e.g. Sept 2026" />
              </div>
              <div>
                <label className={labelCls}>Number of Travelers</label>
                <input type="number" min={1} className={inputCls} value={form.travelers} onChange={(e) => set('travelers', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Travel Style</label>
                <select className={inputCls} value={form.style} onChange={(e) => set('style', e.target.value)}>
                  {TRAVEL_STYLES.map((s) => <option key={s} value={s} className="text-[#1A3B5C]">{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Preferred Destinations</label>
                <select className={inputCls} value={form.destinations} onChange={(e) => set('destinations', e.target.value)}>
                  <option value="" className="text-[#1A3B5C]">No preference</option>
                  {hubRegions.map((r) => <option key={r.id} value={r.name} className="text-[#1A3B5C]">{r.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Budget Range</label>
                <select className={inputCls} value={form.budget} onChange={(e) => set('budget', e.target.value)}>
                  {BUDGETS.map((b) => <option key={b} value={b} className="text-[#1A3B5C]">{b}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls}>Special Interests</label>
              <input className={inputCls} value={form.interests} onChange={(e) => set('interests', e.target.value)} placeholder="Castles, wine, photography, wildlife…" />
            </div>
            <div>
              <label className={labelCls}>Message</label>
              <textarea rows={4} className={inputCls} value={form.message} onChange={(e) => set('message', e.target.value)} placeholder="Tell us about your dream Romania journey…" />
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={smsOptIn}
                onChange={(e) => setSmsOptIn(e.target.checked)}
                className="mt-1 accent-[#C9A961]"
              />
              <span className="font-sans text-[11px] text-white/50 leading-relaxed">
                Text me updates. Msg &amp; data rates may apply. Reply STOP to unsubscribe.
              </span>
            </label>

            {error && <p className="font-sans text-xs text-red-300">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full font-sans text-[11px] tracking-[0.25em] uppercase px-8 py-4 bg-[#C9A961] text-white hover:bg-[#b8944d] transition-colors disabled:opacity-60 flex items-center justify-center gap-3"
            >
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : 'Start Planning With Zera Travel'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default RomaniaInquiry;
